import { useState, useEffect } from 'react'
import { useAuth } from '../context/AuthContext'
import { Navigate, useLocation } from 'react-router-dom'
import { checkSystemReady } from '../services/systemService'
import { LuEye, LuEyeOff } from 'react-icons/lu'
import './LoginPage.css'

function LoginPage() {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [mfaRequired, setMfaRequired] = useState(false)
  const [totpCode, setTotpCode] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [systemReady, setSystemReady] = useState(false)
  const [checkingSystem, setCheckingSystem] = useState(true)
  const [systemMessage, setSystemMessage] = useState('Conectando')
  const [backgroundLoaded, setBackgroundLoaded] = useState(false)
  const [pageReady, setPageReady] = useState(true)
  const [market, setMarket] = useState(() => (localStorage.getItem('flash_market') === 'CL' ? 'CL' : 'AR'))
  const [showPassword, setShowPassword] = useState(false)
  const [fieldErrors, setFieldErrors] = useState({})
  const { login, isAuthenticated } = useAuth()
  const location = useLocation()

  useEffect(() => {
    setBackgroundLoaded(true)
    setPageReady(true)
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    verifySystemReady()
    
    // Si el interceptor de api.js o un guard de auth nos mandó acá por
    // un user de otro portal, mostramos ese mensaje.
    const blockedMsg = (() => {
      try { return localStorage.getItem('login_blocked_message') } catch (_) { return null }
    })()
    if (blockedMsg) {
      setError(blockedMsg)
      try { localStorage.removeItem('login_blocked_message') } catch (_) {}
    } else if (location.state?.message) {
      setError(location.state.message)
    }
    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [])

  useEffect(() => {
    if (systemReady && backgroundLoaded) {
      setPageReady(true)
    }
  }, [systemReady, backgroundLoaded])

  const verifySystemReady = async () => {
    try {
      setCheckingSystem(true)
      const result = await checkSystemReady()
      
      if (result.ready) {
        setSystemReady(true)
        setSystemMessage('')
        setCheckingSystem(false)
      } else {
        setSystemMessage('Conectando')
        setTimeout(verifySystemReady, 2000)
      }
    } catch (err) {
      setSystemMessage('Conectando')
      setTimeout(verifySystemReady, 3000)
    }
  }

  const chooseMarket = (code) => {
    localStorage.setItem('flash_market', code)
    setMarket(code)
    setError('')
  }

  if (isAuthenticated) {
    return <Navigate to={localStorage.getItem('flash_market') === 'CL' ? '/clp' : '/accounts'} replace />
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    const nextErrors = {}
    if (!username.trim()) nextErrors.username = 'Completá tu usuario'
    if (!password) nextErrors.password = 'Completá tu contraseña'
    setFieldErrors(nextErrors)
    if (Object.keys(nextErrors).length) return
    setError('')
    setLoading(true)

    const chosen = market === 'CL' ? 'CL' : 'AR'
    localStorage.setItem('flash_market', chosen)
    const extra = mfaRequired ? { totpCode: totpCode.trim() } : {}
    const result = await login(username, password, extra)

    if (result.mfaRequired) {
      setMfaRequired(true)
      setError('Ingresá el código de Google Authenticator')
    } else if (!result.success) {
      setError(result.error)
    }
    
    setLoading(false)
  }

  return (
    <>
      {!pageReady && (
        <div className="initial-loading">
          <div className="spinner"></div>
          <p className="loading-message">Cargando...</p>
        </div>
      )}

      <div className="login-split">
        <aside className="login-brand">
          <div className="login-brand-top">
            <div className="login-brand-mark" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="28" height="28">
                <path d="M13 2 4 14h7l-1 8 10-14h-7l0-6z" fill="currentColor" />
              </svg>
            </div>
            <p className="login-brand-name">FLASH</p>
            <p className="login-brand-line">Portal de cuentas, movimientos y transferencias.</p>
          </div>
          <p className="login-brand-foot">Argentina · Chile</p>
        </aside>
        <div className="login-form-col">
          <form onSubmit={handleSubmit} className="login-form" noValidate>
            <div className="login-title">
              <p className="login-kicker">Acceso</p>
              <h1>Ingresá a Flash</h1>
            </div>
            <div className="form-group">
              <label htmlFor="market">País</label>
              <select id="market" className="form-control" value={market} onChange={(e) => chooseMarket(e.target.value)}>
                <option value="AR">Argentina</option>
                <option value="CL">Chile</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="username">Usuario</label>
              <input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Ingresá tu usuario"
                autoFocus
                className={`form-control ${fieldErrors.username ? 'is-invalid' : ''}`}
                aria-invalid={!!fieldErrors.username}
              />
              {fieldErrors.username && <p className="field-error">{fieldErrors.username}</p>}
            </div>
            <div className="form-group">
              <label htmlFor="password">Contraseña</label>
              <div className="password-field">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Ingresá tu contraseña"
                  className={`form-control ${fieldErrors.password ? 'is-invalid' : ''}`}
                  aria-invalid={!!fieldErrors.password}
                />
                <button
                  type="button"
                  className="password-toggle"
                  aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                  onClick={() => setShowPassword((v) => !v)}
                >
                  {showPassword ? <LuEyeOff /> : <LuEye />}
                </button>
              </div>
              {fieldErrors.password && <p className="field-error">{fieldErrors.password}</p>}
              <button type="button" className="login-forgot">Olvidé mi contraseña</button>
            </div>
            {mfaRequired && (
              <div className="form-group">
                <label htmlFor="totp">Código de autenticación</label>
                <input
                  id="totp"
                  type="text"
                  inputMode="numeric"
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value)}
                  placeholder="123456"
                  className="form-control"
                  autoFocus
                />
              </div>
            )}
            {checkingSystem && <p className="login-status">Conectando</p>}
            {error && <div className="error-message" role="alert">{error}</div>}
            <button type="submit" className="btn-primary login-btn" disabled={loading || checkingSystem}>
              {loading ? 'Ingresando…' : 'Ingresar'}
            </button>
          </form>
        </div>
      </div>
    </>
  )
}

export default LoginPage
